import { createRouter, createWebHistory } from 'vue-router'
import VueBodyClass from 'vue-body-class';
import { dialog } from '@/plugins/dialog'

import HomeView from '../views/HomeView.vue'

// global styles from the removed legacy auth pages that other components still use
import '@/assets/scss/legacy-auth-pages.scss'
import LoginView from '../views/LoginView.vue'
import JoinView from '../views/JoinView.vue'
// global styles from the removed ProfileView that other components still use
import '@/assets/scss/legacy-profile-page.scss'
import RoomView from '../views/RoomView.vue'
import PaymentSuccess from "@/views/Website/PaymentSuccess";
import PaymentCancel from "@/views/Website/PaymentCancel";
import SessionLogs from '@/views/SessionLogs.vue';

// Website Views
// global styles from the removed AboutView that other website pages still use
import '@/assets/scss/legacy-about-page.scss'
import FaqView from '../views/Website/FaqView.vue'
import TermsView from '../views/Website/TermsView'
import GlossaryView from '../views/Website/GlossaryView'
import TranscriptsView from '../views/Website/TranscriptsView.vue';

import store from '../store/'
import { toast } from '@/plugins/toast'
import emitter from '@/services/emitter.service'
import VersionView from "@/views/Website/VersionView.vue";
import AccountConfirm from "@/views/AccountConfirm.vue";
import PlansView from "@/views/Website/PlansView.vue";
import SharedTranscriptView from "@/views/Website/SharedTranscriptView.vue";
import OrganizationView from "@/views/Website/OrganizationView.vue";
import OrganizationMembersView from "@/views/Website/OrganizationMembersView.vue";
import OrganizationVocabularyView from "@/views/Website/OrganizationVocabularyView.vue";


const routes = [
  {
    path: '/',
    name: 'home',
    component: HomeView
  },
  {
    path: '/login',
    name: 'login',
    component: LoginView
  },
  {
    path: '/faq',
    name: 'faq',
    component: FaqView,
    meta: {
      tpl: 'website'
    }
  },
  {
    path: '/plans/:campaign_name?',
    name: 'plans',
    component: PlansView,
    meta: {
      tpl: 'website'
    }
  },
  {
    path: '/join',
    name: 'join',
    component: JoinView
  },
  {
    path: '/account-confirm',
    name: 'account-confirm',
    component: AccountConfirm,
  },
  {
    path: '/room/:room_code',
    name: 'room',
    component: RoomView,
    meta: {
      tpl: 'conference',
      bodyClass: 'conferenceBody'
    },
  },
  {
    path: '/payment_success',
    name: 'payment_success',
    component: PaymentSuccess,
    meta: {
      tpl: 'website'
    }
  },
  {
    path: '/payment_cancel',
    name: 'payment_cancel',
    component: PaymentCancel,
    meta: {
      tpl: 'website'
    }
  },
  {
    path: '/terms',
    name: 'terms',
    component: TermsView,
    meta: {
      tpl: 'website'
    }
  },
  {
    path: '/glossary',
    name: 'glossary',
    component: GlossaryView,
    meta: {
      tpl: 'website'
    }
  },
  {
    path: '/transcripts',
    name: 'transcripts',
    component: TranscriptsView,
    meta: {
      tpl: 'website'
    }
  },
  {
    path: '/transcripts/:id',
    name: 'transcript',
    component: TranscriptsView,
    meta: {
      tpl: 'website'
    }
  },
  {
    path: '/share/transcript/:id',
    name: 'share-transcript',
    component: SharedTranscriptView,
    meta: {
      tpl: 'website'
    }
  },
  {
    path: '/version',
    name: 'version',
    component: VersionView,
    meta: {
      tpl: 'website'
    }
  },
  {
    path: '/organization',
    component: OrganizationView,
    meta: {
      tpl: 'website',
      requiresAuth: true,
      requiresOrganizationAdmin: true
    },
    children: [
      {
        path: '',
        name: 'organization',
        component: OrganizationMembersView
      },
      {
        path: 'vocabulary',
        name: 'organization-vocabulary',
        component: OrganizationVocabularyView
      }
    ]
  },
  {
    path: "/icons",
    name: "icons-docs",
    component: () => import("@/views/IconsDocsView.vue"),
    meta: {
      tpl: 'website'
    }
  },
  {
    path: '/logs',
    name: 'logs',
    component: SessionLogs,
    meta: {
      tpl: 'conference',
    },
  },
]

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes
})

const vueBodyClass = new VueBodyClass(routes);

// `nonpersist.user` (and thus organization info) is only populated once the app's
// initial `userMe` fetch resolves (see plugins/forumAuth.ts), unlike `auth.authorized`
// which is persisted to localStorage and available synchronously. Routes that need
// organization info have to wait for that fetch to finish before checking it.
function waitForPrefetch() {
  if (store.state.nonpersist.prefetched) return Promise.resolve();

  return new Promise(resolve => {
    const unwatch = store.watch(
      state => state.nonpersist.prefetched,
      prefetched => {
        if (prefetched) {
          unwatch();
          resolve();
        }
      }
    );
  });
}

function isOrganizationAdmin() {
  const organization = store.getters['nonpersist/getUserOrganization'];
  return !!(organization && organization.id && organization.role === 'admin');
}

router.beforeEach(async (to, from, next) => {
  // Close any alert / confirm dialogs left open
  dialog.closeAll();

  const requiresAuth = to.matched.some(record => record.meta.requiresAuth);
  const requiresOrganizationAdmin = to.matched.some(record => record.meta.requiresOrganizationAdmin);

  if (requiresAuth && !store.state.auth.authorized) {
    // Auth0 login is a full-page redirect (see App.vue's handleAuth0Redirect,
    // triggered the same way from HomeView/WebsiteHeader/PlansView), so the
    // in-app navigation itself is cancelled rather than sent to another route.
    emitter.emit('open-auth0');
    next(false);
    return;
  }

  if (requiresOrganizationAdmin) {
    await waitForPrefetch();

    if (!isOrganizationAdmin()) {
      toast.error("You don't have access to organization management");
      next({ name: 'home' });
      return;
    }
  }

  // Apply vueBodyClass guard last - it calls next() itself to continue the navigation.
  vueBodyClass.guard(to, next);
});

export default router
